import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Plus, FileText, Edit2, Eye, Copy, Archive, ArchiveRestore } from 'lucide-react';
import { useMailTemplates } from '../../hooks/useMailTemplates';
import { mailTemplatesService } from '../../services/mail-templates.service';
import { useProduct } from '../../contexts/ProductContext';
import { Card, Button, EmptyState, Skeleton } from '../../ui';
import { EmailPreviewModal } from '../mail/EmailPreview';

interface ActionIconButtonProps {
  onClick: (e: React.MouseEvent) => void;
  title: string;
  className?: string;
  children: React.ReactNode;
}

function ActionIconButton({ onClick, title, className = '', children }: ActionIconButtonProps) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={`p-1.5 rounded-md text-ink-tertiary hover:text-ink-primary hover:bg-subtle transition-colors ${className}`}
    >
      {children}
    </button>
  );
}

export function TemplatesGridEmbed() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { selectedProductId } = useProduct();
  const { data, isLoading } = useMailTemplates(selectedProductId ?? 0, { limit: 50 });
  const [previewTemplate, setPreviewTemplate] = useState<any | null>(null);

  const duplicateMutation = useMutation({
    mutationFn: (id: number) => mailTemplatesService.duplicate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mail-templates'] });
      toast.success('Template duplicado');
    },
    onError: () => toast.error('Não foi possível duplicar o template.'),
  });

  const archiveMutation = useMutation({
    mutationFn: ({ id, active }: { id: number; active: boolean }) =>
      mailTemplatesService.update(id, { active }),
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: ['mail-templates'] });
      toast.success(vars.active ? 'Template restaurado' : 'Template arquivado');
    },
    onError: () => toast.error('Não foi possível atualizar o template.'),
  });

  if (!selectedProductId) {
    return <EmptyState title="Selecione um produto" body="Templates são por produto." />;
  }

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} height={120} />
        ))}
      </div>
    );
  }

  const templates: any[] =
    (data as any)?.data ?? (data as any)?.templates ?? (Array.isArray(data) ? data : []);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-ink-secondary">
          {templates.length} template{templates.length !== 1 ? 's' : ''}
        </p>
        <Button
          size="sm"
          icon={<Plus className="w-4 h-4" />}
          onClick={() => navigate('/campanhas/templates/new/edit')}
        >
          Novo template
        </Button>
      </div>
      {templates.length === 0 ? (
        <EmptyState
          illustration={<FileText className="w-12 h-12" />}
          title="Sem templates"
          body="Crie um template pra começar."
          action={
            <Button size="sm" onClick={() => navigate('/campanhas/templates/new/edit')}>
              Criar template
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {templates.map((t: any) => {
            const isArchived = t.active === false;
            return (
              <Card
                key={t.id}
                padding="md"
                className={`group cursor-pointer hover:shadow-md transition-opacity ${isArchived ? 'opacity-60' : ''}`}
                onClick={() => navigate(`/campanhas/templates/${t.id}/edit`)}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <h3 className="font-medium text-ink-primary truncate">
                        {t.name || 'Sem nome'}
                      </h3>
                      {isArchived && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-subtle text-ink-tertiary font-medium uppercase tracking-wider">
                          Arquivado
                        </span>
                      )}
                    </div>
                    {t.subject && (
                      <p className="text-xs text-ink-tertiary mt-0.5 truncate">
                        Assunto: {t.subject}
                      </p>
                    )}
                  </div>
                  <Edit2 className="w-4 h-4 text-ink-tertiary shrink-0 mt-0.5" />
                </div>
                <div className="flex items-center gap-0.5 mt-3 -mb-1 -ml-1.5">
                  <ActionIconButton
                    title="Pré-visualizar"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPreviewTemplate(t);
                    }}
                  >
                    <Eye className="w-3.5 h-3.5" />
                  </ActionIconButton>
                  <ActionIconButton
                    title="Duplicar template"
                    onClick={(e) => {
                      e.stopPropagation();
                      duplicateMutation.mutate(t.id);
                    }}
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </ActionIconButton>
                  <ActionIconButton
                    title={isArchived ? 'Restaurar template' : 'Arquivar template'}
                    onClick={(e) => {
                      e.stopPropagation();
                      archiveMutation.mutate({ id: t.id, active: isArchived });
                    }}
                  >
                    {isArchived ? (
                      <ArchiveRestore className="w-3.5 h-3.5" />
                    ) : (
                      <Archive className="w-3.5 h-3.5" />
                    )}
                  </ActionIconButton>
                </div>
              </Card>
            );
          })}
        </div>
      )}
      <EmailPreviewModal
        blocks={Array.isArray(previewTemplate?.blocks) ? previewTemplate.blocks : []}
        open={previewTemplate !== null}
        onClose={() => setPreviewTemplate(null)}
      />
    </div>
  );
}

export default TemplatesGridEmbed;
